import { Link, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Hourglass, Lightbulb } from '@phosphor-icons/react';

// Metadata for each upcoming tool. Order matches TemplatesSection.jsx.
// When a tool is ready, remove its entry here AND update the link in
// TemplatesSection.jsx — nothing else needs to change.
const TOOL_META = {
  analysis: {
    title: 'أداة التحليل',
    titleEn: 'Analysis Tool',
    category: 'قوالب الجودة',
  },
  improvement: {
    title: 'أداة خطة التحسين',
    titleEn: 'Improvement Plan Tool',
    category: 'قوالب التشغيل',
  },
  monitoring: {
    title: 'أداة المتابعة وقياس الأداء',
    titleEn: 'Performance Tracking Tool',
    category: 'قوالب التشغيل',
  },
  accreditation: {
    title: 'أداة الاستعداد للاعتماد',
    titleEn: 'Accreditation Readiness Tool',
    category: 'قوالب الجودة',
  },
};

export default function ToolComingSoonPage() {
  const { toolId } = useParams();
  const { hash } = useLocation();
  const meta = TOOL_META[toolId] || {
    title: 'الأداة الكاملة',
    titleEn: 'Full Tool',
    category: '',
  };

  // Preserve #resources anchor when navigating back so the user lands
  // exactly at the templates section, not the top of the homepage.
  const backHref = '/#templates';

  return (
    <main
      dir="rtl"
      className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4 py-24"
      data-testid={`tool-coming-soon-${toolId}`}
    >
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-3xl bg-white border border-[#E6E0D6] rounded-3xl shadow-[0_8px_40px_rgba(31,61,43,0.05)] px-6 py-14 sm:px-14 sm:py-16 text-center"
      >
        {/* Category badge */}
        {meta.category && (
          <p
            className="text-[11px] tracking-[3px] uppercase text-[#7A866E] mb-6"
            data-testid="tool-category"
          >
            {meta.category}
          </p>
        )}

        {/* Icon */}
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#F5F2EC] text-[#1F3D2B] mb-6">
          <Hourglass size={32} weight="duotone" />
        </div>

        {/* Title */}
        <h1
          className="text-3xl sm:text-4xl font-semibold text-[#1F3D2B] mb-2 leading-tight"
          data-testid="tool-title"
        >
          {meta.title}
        </h1>
        <p className="text-sm text-[#7A866E] tracking-widest mb-10">
          {meta.titleEn}
        </p>

        {/* Status line */}
        <div className="inline-flex items-center gap-2 bg-[#F5F2EC] text-[#3D512D] text-sm font-medium px-4 py-2 rounded-full mb-8">
          <span className="w-2 h-2 rounded-full bg-[#8B6B00]" />
          هذه الأداة قيد الإعداد وستكون متاحة قريبًا
        </div>

        {/* Body copy */}
        <p className="text-[15px] leading-[2] text-[#2A3927] max-w-xl mx-auto mb-10">
          نعمل على تطوير نسخة احترافية مستقلة لهذه الأداة، بمحتواها، وملفاتها،
          وتصميمها الخاص — بما يضمن تقديم أفضل تجربة ممكنة لعملائنا حين إطلاقها.
        </p>

        {/* Small consultative hint */}
        <div className="flex items-start gap-3 bg-[#FAFAF7] border border-[#E6E0D6] rounded-2xl px-5 py-4 text-right mb-10 max-w-xl mx-auto">
          <Lightbulb
            size={20}
            weight="duotone"
            className="text-[#4A641F] mt-0.5 flex-shrink-0"
          />
          <p className="text-[13px] leading-[1.9] text-[#3D512D]">
            في هذه الأثناء، يمكنكم البدء بـ
            {' '}
            <Link
              to="/contact"
              className="underline underline-offset-4 text-[#1F3D2B] hover:text-[#163020] font-medium"
              data-testid="tool-suggest-diagnosis"
            >
              أداة التقييم الذاتي الشامل
            </Link>
            {' '}
            الجاهزة حاليًا، أو التواصل معنا لمناقشة احتياجاتكم.
          </p>
        </div>

        {/* Back to resources */}
        <Link
          to={backHref}
          data-testid="tool-back-to-resources"
          className="inline-flex items-center gap-2 bg-[#1F3D2B] hover:bg-[#163020] text-white text-sm font-medium px-6 py-3 rounded-xl transition-all duration-200"
        >
          <ArrowLeft size={16} className="rtl-flip" />
          العودة إلى صفحة الموارد
        </Link>
      </motion.section>
    </main>
  );
}
